import React, { useState } from 'react';
import { Calculator, Shield, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';

/**
 * RiskCalculator Component
 * 
 * A reusable compliance risk estimator that calculates potential EPA/ADEC penalties
 * based on violation type, facility size, compliance history, and discovery method.
 * 
 * Features:
 * - Interactive calculator with dropdown selectors and slider
 * - Real-time penalty range calculation
 * - EPA Audit Policy integration for voluntary disclosure
 * - Comprehensive educational content about compliance costs
 * - Sales-focused messaging to emphasize value of preventive services
 */
const RiskCalculator = () => {
  // Calculator State
  const [violationType, setViolationType] = useState('administrative');
  const [facilitySize, setFacilitySize] = useState('minor');
  const [complianceHistory, setComplianceHistory] = useState('clean');
  const [discoveryMethod, setDiscoveryMethod] = useState('inspection');
  const [daysOutOfCompliance, setDaysOutOfCompliance] = useState(7);

  // Realistic Base Costs (based on typical EPA/ADEC settlements, not statutory max)
  const baseCosts = {
    administrative: 2000,    // Late reports, minor paperwork issues
    exceedance: 10000,      // Permit limit violations, opacity issues
    release: 30000          // Unpermitted equipment, major releases
  };

  // Facility Size Multipliers
  const multipliers = {
    minor: 1.0,    // Minor sources
    major: 2.5     // Title V/Major sources (higher scrutiny)
  };

  // Compliance History Multipliers
  const historyMultipliers = {
    clean: 1.0,              // Clean record
    priorViolations: 1.5     // Prior violations increase penalty
  };

  // Discovery Method Multipliers
  const discoveryMultipliers = {
    inspection: 1.0,          // Found by agency - full penalty
    voluntary: 0.1            // 90% reduction for voluntary disclosure (EPA Audit Policy)
  };

  // Calculate Estimated Risk with all factors
  const baseCost = baseCosts[violationType] || 2000;
  const sizeMultiplier = multipliers[facilitySize] || 1.0;
  const historyMultiplier = historyMultipliers[complianceHistory] || 1.0;
  const discoveryMultiplier = discoveryMultipliers[discoveryMethod] || 1.0;
  
  // Calculate base risk first
  const baseRisk = baseCost * sizeMultiplier * daysOutOfCompliance * historyMultiplier;
  
  // Apply EPA Audit Policy potential for voluntary disclosure
  let centerEstimate, lowerEstimate, upperEstimate;
  
  if (discoveryMethod === 'voluntary') {
    // EPA Audit Policy: CAN waive 100% but requires meeting all 9 conditions
    // Show RANGE from best case (waiver) to worst case (improper disclosure)
    lowerEstimate = 0;                          // Best case: Full waiver achieved
    upperEstimate = Math.round(baseRisk * 1.2); // Worst case: Failed to meet conditions
    centerEstimate = Math.round((lowerEstimate + upperEstimate) / 2);
  } else {
    // Standard agency inspection - full penalty
    centerEstimate = baseRisk * discoveryMultiplier;
    lowerEstimate = Math.round(centerEstimate * 0.8);
    upperEstimate = Math.round(centerEstimate * 1.2);
  }

  // Color coding based on risk level and discovery method
  const getRiskColor = () => {
    if (discoveryMethod === 'voluntary') return 'text-blue-700'; // Blue for voluntary (potential)
    if (upperEstimate < 10000) return 'text-blue-600';
    if (upperEstimate < 50000) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border-2 border-blue-200 rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-white">
            <div className="flex items-center mb-4">
              <Calculator className="w-12 h-12 mr-4" />
              <div>
                <h2 className="text-3xl font-bold">Compliance Risk Estimator</h2>
                <p className="text-blue-100 mt-2">
                  Calculate the potential exposure of non-compliance events based on your facility profile. 
                  Estimates reflect typical EPA/ADEC settlement ranges, not statutory maximums.
                </p>
              </div>
            </div>
          </div>

          {/* Calculator Body */}
          <div className="p-8">
            {/* Input Section - 2x2 Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Row 1: Violation Type */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  Violation Type
                </label>
                <select
                  value={violationType}
                  onChange={(e) => setViolationType(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 font-medium"
                >
                  <option value="administrative">Administrative (Late Reports, Paperwork)</option>
                  <option value="exceedance">Permit Limit Exceedance (Opacity, Emissions)</option>
                  <option value="release">Major Release (Unpermitted Equipment)</option>
                </select>
                <p className="mt-2 text-sm text-gray-600">
                  {violationType === 'administrative' && 'Base: $2,000/day'}
                  {violationType === 'exceedance' && 'Base: $10,000/day'}
                  {violationType === 'release' && 'Base: $30,000/day'}
                </p>
              </div>

              {/* Row 1: Facility Classification */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  Facility Classification
                </label>
                <select
                  value={facilitySize}
                  onChange={(e) => setFacilitySize(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 font-medium"
                >
                  <option value="minor">Minor Source (1.0x)</option>
                  <option value="major">Major/Title V Source (2.5x)</option>
                </select>
                <p className="mt-2 text-sm text-gray-600">
                  {facilitySize === 'minor' && 'Standard enforcement'}
                  {facilitySize === 'major' && 'Higher scrutiny'}
                </p>
              </div>

              {/* Row 2: History of Compliance */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  History of Compliance
                </label>
                <select
                  value={complianceHistory}
                  onChange={(e) => setComplianceHistory(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 font-medium"
                >
                  <option value="clean">Clean Record (1.0x)</option>
                  <option value="priorViolations">Prior Violations within 5 Years (1.5x)</option>
                </select>
                <p className="mt-2 text-sm text-gray-600">
                  {complianceHistory === 'clean' && 'No penalty increase'}
                  {complianceHistory === 'priorViolations' && 'Repeat violator penalty'}
                </p>
              </div>

              {/* Row 2: Discovery Method */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  Discovery Method
                </label>
                <select
                  value={discoveryMethod}
                  onChange={(e) => setDiscoveryMethod(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 font-medium"
                >
                  <option value="inspection">Agency Inspection (1.0x)</option>
                  <option value="voluntary">Voluntary Self-Disclosure (0.1x)</option>
                </select>
                <p className="mt-2 text-sm text-gray-600">
                  {discoveryMethod === 'inspection' && 'Standard penalty - full exposure'}
                  {discoveryMethod === 'voluntary' && '90% penalty reduction - dramatic savings'}
                </p>
              </div>
            </div>

            {/* Duration Slider - Full Width */}
            <div className="mb-8">
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Duration of Non-Compliance
              </label>
              <div className="flex items-center space-x-6">
                <input
                  type="range"
                  min="1"
                  max="60"
                  value={daysOutOfCompliance}
                  onChange={(e) => setDaysOutOfCompliance(parseInt(e.target.value))}
                  className="flex-1 h-3 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #2563eb 0%, #2563eb ${(daysOutOfCompliance / 60) * 100}%, #dbeafe ${(daysOutOfCompliance / 60) * 100}%, #dbeafe 100%)`
                  }}
                />
                <div className="w-24 text-center bg-blue-50 rounded-lg p-2">
                  <span className="text-2xl font-bold text-blue-700">{daysOutOfCompliance}</span>
                  <p className="text-xs text-gray-600">days</p>
                </div>
              </div>
            </div>

            {/* Output Section - Risk Estimate */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 border-2 border-blue-300 rounded-xl p-10 mb-6">
              <div className="text-center">
                <p className="text-lg text-gray-700 mb-3 font-semibold">Projected Financial Liability:</p>
                <div className="text-5xl font-black text-red-600 mb-4">
                  ${lowerEstimate.toLocaleString()} - ${upperEstimate.toLocaleString()}{discoveryMethod === 'voluntary' ? '*' : ''}
                </div>
                {discoveryMethod === 'voluntary' ? (
                  <div className="flex items-center justify-center space-x-2 text-gray-700 mb-4">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <p className="text-sm font-semibold">
                      *Best-case outcome ($0) requires meeting ALL EPA Audit Policy conditions with expert guidance
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2 text-gray-600 mb-4">
                    <Shield className="w-5 h-5 text-blue-600" />
                    <p className="text-sm">
                      Based on historical EPA/ADEC settlement data
                    </p>
                  </div>
                )}
                <div className="bg-white rounded-lg p-4 inline-block">
                  <p className="text-sm text-gray-500 italic" style={{ fontSize: '0.85rem' }}>
                    <strong>Calculation:</strong> {violationType === 'administrative' ? 'Administrative' : violationType === 'exceedance' ? 'Exceedance' : 'Major Release'} 
                    {' '}(${baseCost.toLocaleString()}/day) × {facilitySize === 'minor' ? 'Minor' : 'Major'} 
                    {' '}({sizeMultiplier}x) × {complianceHistory === 'clean' ? 'Clean' : 'Prior Violations'} 
                    {' '}({historyMultiplier}x) × {discoveryMethod === 'inspection' ? 'Inspection' : 'Voluntary'} 
                    {' '}({discoveryMultiplier}x) × {daysOutOfCompliance} days
                  </p>
                </div>
              </div>
            </div>

            {/* Voluntary Disclosure Tip - Strong Sales Message */}
            {discoveryMethod === 'voluntary' && (
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 border-2 border-blue-800 px-6 py-6 md:p-8 mb-6 shadow-xl">
                <div className="flex flex-col md:flex-row items-start md:space-x-4">
                  <Shield className="w-12 h-12 text-white flex-shrink-0 mt-1 hidden md:block" />
                  <div className="w-full">
                    <p className="text-2xl font-bold text-white mb-4 text-left">
                      ⚠️ Can You Achieve the $0 Penalty on Your Own?
                    </p>
                    <p className="text-blue-50 text-lg font-semibold mb-4 text-left">
                      The EPA Audit Policy <strong className="text-white">can waive 100% of penalties</strong>&mdash;but only if you meet ALL 9 strict conditions:
                    </p>
                    <ul className="text-blue-50 space-y-2 mb-6">
                      <li className="flex flex-row items-start gap-3">
                        <span className="flex-none mt-1">✓</span>
                        <span className="flex-1 text-left"><strong>Systematic discovery</strong> through environmental audit or compliance management system</span>
                      </li>
                      <li className="flex flex-row items-start gap-3">
                        <span className="flex-none mt-1">✓</span>
                        <span className="flex-1 text-left"><strong>Prompt disclosure</strong> within 21 days of discovery</span>
                      </li>
                      <li className="flex flex-row items-start gap-3">
                        <span className="flex-none mt-1">✓</span>
                        <span className="flex-1 text-left"><strong>Independent discovery</strong> (not prompted by agency request or third-party report)</span>
                      </li>
                      <li className="flex flex-row items-start gap-3">
                        <span className="flex-none mt-1">✓</span>
                        <span className="flex-1 text-left"><strong>Correction within 60 days</strong> (or expeditious schedule approved by EPA)</span>
                      </li>
                      <li className="flex flex-row items-start gap-3">
                        <span className="flex-none mt-1">✓</span>
                        <span className="flex-1 text-left"><strong>Prevent recurrence</strong> with documented corrective actions</span>
                      </li>
                      <li className="flex flex-row items-start gap-3">
                        <span className="flex-none mt-1">✓</span>
                        <span className="flex-1 text-left">...and 4 additional procedural requirements</span>
                      </li>
                    </ul>
                    <div className="bg-white/20 backdrop-blur-sm border-2 border-white rounded-lg p-5 mt-4">
                      <p className="text-white text-lg font-bold mb-3 text-left">
                        ⚡ The Reality: Missing ANY condition means <span className="text-yellow-300">full penalties apply</span>
                      </p>
                      <p className="text-blue-50 font-medium text-left">
                        Without expert guidance, facilities often fail on technicalities: improper documentation, 
                        missing the 21-day window, inadequate corrective action plans, or incorrect disclosure procedures. 
                        One misstep transforms a <strong className="text-white">$0 settlement</strong> into the 
                        <strong className="text-white"> ${upperEstimate.toLocaleString()}+ penalty range</strong> shown above.
                      </p>
                    </div>
                    <div className="mt-6">
                      <p className="text-white text-xl font-bold mb-3 text-left">
                        🛡️ How Raven Environmental Helps You Achieve the Best Outcome:
                      </p>
                      <ul className="text-blue-50 space-y-2">
                        <li className="flex flex-row items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-300 flex-none mt-1" />
                          <span className="flex-1 text-left"><strong>Systematic audit protocols</strong> that satisfy EPA&apos;s &ldquo;discovery&rdquo; requirements</span>
                        </li>
                        <li className="flex flex-row items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-300 flex-none mt-1" />
                          <span className="flex-1 text-left"><strong>Proper disclosure documentation</strong> meeting all 9 conditions</span>
                        </li>
                        <li className="flex flex-row items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-300 flex-none mt-1" />
                          <span className="flex-1 text-left"><strong>Expedited corrective action plans</strong> with agency coordination</span>
                        </li>
                        <li className="flex flex-row items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-300 flex-none mt-1" />
                          <span className="flex-1 text-left"><strong>Ongoing prevention systems</strong> to demonstrate good faith</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Financial Context Section */}
            <div className="bg-white border-2 border-gray-300 rounded-xl p-8 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                Context vs. Prevention
              </h3>
              
              <div className="space-y-4">
                {/* Cost of Inaction */}
                <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                  <div className="flex flex-col w-full gap-2">
                    {/* TOP ROW: Icon + Text */}
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">🔴</span>
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900 text-sm leading-tight">
                          Cost of Inaction (Est. Avg)
                        </span>
                        <span className="text-xs text-gray-500">
                          Likely settlement range
                        </span>
                      </div>
                    </div>
                    {/* BOTTOM ROW: Price (Aligned Right) */}
                    <div className="text-right">
                      <span className={`font-black text-2xl ${getRiskColor()}`}>
                        ${Math.round((lowerEstimate + upperEstimate) / 2).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Reactive Legal Defense */}
                <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                  <div className="flex flex-col w-full gap-2">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">⚖️</span>
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900 text-sm leading-tight">
                          Reactive Legal Defense
                        </span>
                        <span className="text-xs text-gray-500">
                          Retainers + crisis management
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-black text-2xl text-orange-600">
                        $15,000+
                      </span>
                    </div>
                  </div>
                </div>

                {/* Proactive Compliance Audit */}
                <div className="p-4 bg-emerald-50 rounded-lg border-l-4 border-emerald-500">
                  <div className="flex flex-col w-full gap-2">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">🛡️</span>
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900 text-sm leading-tight">
                          Proactive Compliance Audit
                        </span>
                        <span className="text-xs text-gray-500">
                          One-time vulnerability check
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-black text-2xl text-emerald-600">
                        $3,500
                      </span>
                    </div>
                  </div>
                </div>

                {/* Continuous Prevention Program */}
                <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                  <div className="flex flex-col w-full gap-2">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">📊</span>
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900 text-sm leading-tight">
                          Continuous Prevention Program
                        </span>
                        <span className="text-xs text-gray-500">
                          Ongoing monitoring & reporting
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-black text-2xl text-blue-600">
                        $2K-5K/mo
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ROI Message */}
              <div className="mt-6 pt-6 border-t-2 border-gray-200 text-center">
                <p className="text-gray-700 text-lg">
                  <strong>ROI: Prevention is cheaper than the cure.</strong> {
                    (() => {
                      const avgPenalty = Math.round((lowerEstimate + upperEstimate) / 2);
                      const auditCost = 3500;
                      const percentage = Math.round((auditCost / avgPenalty) * 100);
                      
                      if (percentage > 20) {
                        return 'A single audit is a fraction of the cost of potential liability.';
                      } else {
                        return `A single audit costs just ~${percentage}% of your potential liability.`;
                      }
                    })()
                  }
                </p>
              </div>
            </div>

            {/* Value Anchor - The Crucial Comparison */}
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-l-4 border-emerald-600 p-6 mb-6">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-8 h-8 text-emerald-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg font-bold text-gray-900 mb-3">
                    The Math is Clear
                  </p>
                  <p className="text-gray-700 text-left">
                    Even a "minor" violation costs {Math.round(((lowerEstimate + upperEstimate) / 2) / 3500)}x more than proactive support. Our monitoring programs provide insurance against six-figure surprises.
                  </p>
                </div>
              </div>
            </div>

            {/* Methodology Note */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-2">Understanding This Estimate</p>
                  <p className="text-sm text-gray-700 text-left" style={{ lineHeight: '1.6' }}>
                    This calculator uses base penalty rates derived from recent EPA/ADEC settlement data, 
                    not statutory maximums. Actual penalties depend on multiple factors including: 
                    violation severity, economic benefit gained, compliance history, good faith efforts, 
                    and ability to pay. Major sources face higher scrutiny due to their potential environmental impact.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-8 text-center">
              <a
                href="#contact-form"
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
              >
                Schedule Risk Assessment
                <ArrowRight className="ml-2" size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RiskCalculator;